import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchIssue,
  updateIssue,
  deleteIssue,
  fetchComments,
  createComment,
  updateComment,
  deleteComment,
} from "@/lib/api";
import { toast } from "sonner";
import {
  STATUSES,
  STATUS_LABELS,
  STATUS_COLORS,
  PRIORITIES,
  PRIORITY_COLORS,
  ISSUE_TYPE_CONFIG,
  isPastDate,
  ENVIRONMENTS,
  ENV_COLORS,
} from "@/lib/constants";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  X,
  CalendarDays,
  ArrowUpRight,
  Send,
  Link2,
  Pencil,
  Trash2,
  Check,
} from "lucide-react";
import { format } from "date-fns";
import MentionInput from "@/components/comments/MentionInput";
import CommentContent from "@/components/comments/CommentContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function IssuePanel({ issueKey, users, currentUser, onClose, onUpdated, onDeleted }) {
  const navigate = useNavigate();
  const [issue, setIssue] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [deleting, setDeleting] = useState(false);

  const loadIssue = useCallback(async () => {
    try {
      setLoading(true);
      const [issueData, commentsData] = await Promise.all([
        fetchIssue(issueKey),
        fetchComments(issueKey),
      ]);
      setIssue(issueData);
      setTitleValue(issueData.title);
      setDescription(issueData.description || "");
      setComments(commentsData.items || commentsData);
    } catch (e) {
      console.error("Failed to load issue", e);
    } finally {
      setLoading(false);
    }
  }, [issueKey]);

  useEffect(() => {
    loadIssue();
  }, [loadIssue]);

  const handleUpdate = async (field, value) => {
    const prev = issue;
    setIssue((old) => (old ? { ...old, [field]: value } : old));
    try {
      await updateIssue(issueKey, { [field]: value, version: issue?.version });
      onUpdated();
    } catch (e) {
      console.error("Failed to update issue", e);
      setIssue(prev);
      if (e?.response?.status === 409) {
        await loadIssue();
      }
    }
  };

  const handleTitleBlur = () => {
    setEditingTitle(false);
    if (titleValue !== issue?.title) {
      handleUpdate("title", titleValue);
    }
  };

  const handleDescriptionBlur = () => {
    if (description !== issue?.description) {
      handleUpdate("description", description);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentUser) return;
    try {
      await createComment(issueKey, {
        author_id: currentUser.id,
        content: newComment.trim(),
      });
      setNewComment("");
      const updated = await fetchComments(issueKey);
      setComments(updated.items || updated);
    } catch (e) {
      console.error("Failed to add comment", e);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editingCommentContent.trim()) return;
    try {
      await updateComment(commentId, { content: editingCommentContent.trim() });
      setEditingCommentId(null);
      setEditingCommentContent("");
      const updated = await fetchComments(issueKey);
      setComments(updated.items || updated);
    } catch (e) {
      console.error("Failed to edit comment", e);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      const updated = await fetchComments(issueKey);
      setComments(updated.items || updated);
    } catch (e) {
      console.error("Failed to delete comment", e);
    }
  };

  const handleDeleteIssue = async () => {
    try {
      setDeleting(true);
      await deleteIssue(issueKey);
      toast.success("Issue deleted");
      onDeleted?.();
    } catch (e) {
      console.error("Failed to delete issue", e);
      toast.error(e.response?.data?.detail || "Failed to delete issue");
    } finally {
      setDeleting(false);
    }
  };

  const getUserById = (id) => users.find((u) => u.id === id);

  if (loading || !issue) {
    return (
      <>
        <div className="fixed inset-0 bg-brand/20 z-40" onClick={onClose} />
        <div className="fixed inset-y-0 right-0 w-full md:w-[600px] lg:w-[700px] bg-white border-l border-gray-200 shadow-2xl z-50 flex items-center justify-center panel-slide-in">
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      </>
    );
  }

  const typeConf = ISSUE_TYPE_CONFIG[issue.type] || {};

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-brand/20 backdrop-blur-sm z-40"
        onClick={onClose}
        data-testid="issue-panel-overlay"
      />

      {/* Panel */}
      <div
        className="fixed inset-y-0 right-0 w-full md:w-[600px] lg:w-[700px] bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col panel-slide-in"
        data-testid="issue-panel"
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span
              className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm"
              style={{ backgroundColor: typeConf.bg, color: typeConf.text }}
            >
              {typeConf.label}
            </span>
            <span className="text-sm font-mono font-bold text-gray-500">
              {issue.issue_key}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  data-testid="issue-delete-btn"
                  className="p-1 hover:bg-red-50 rounded-sm transition-colors"
                  title="Delete issue"
                >
                  <Trash2 className="w-5 h-5 text-gray-400 hover:text-red-600" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete issue?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {issue.child_issue_keys?.length > 0 ? (
                      <>
                        This will permanently delete <span className="font-semibold text-black">{issue.issue_key}</span>{" "}
                        and its {issue.child_issue_keys.length} linked child task
                        {issue.child_issue_keys.length !== 1 ? "s" : ""}, plus all comments. This cannot be undone.
                      </>
                    ) : (
                      <>
                        This will permanently delete <span className="font-semibold text-black">{issue.issue_key}</span>{" "}
                        and all its comments. This cannot be undone.
                      </>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="issue-delete-confirm"
                    onClick={handleDeleteIssue}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700 rounded-sm"
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <button
              data-testid="issue-panel-close"
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-sm transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            {/* Title */}
            {editingTitle ? (
              <input
                data-testid="issue-title-input"
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => e.key === "Enter" && handleTitleBlur()}
                autoFocus
                className="text-xl font-bold w-full border-b-2 border-black outline-none pb-1 bg-transparent"
                style={{ fontFamily: "Chivo, sans-serif" }}
              />
            ) : (
              <h2
                data-testid="issue-title"
                onClick={() => setEditingTitle(true)}
                className="text-xl font-bold cursor-text hover:bg-gray-50 px-1 -mx-1 py-0.5 rounded-sm transition-colors"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                {issue.title}
              </h2>
            )}

            {/* Parent link */}
            {issue.parent_issue_key && issue.parent && (
              <button
                data-testid="go-to-initiative-btn"
                onClick={() => {
                  onClose();
                  navigate(`/project/${issue.project_key}/initiatives?issue=${issue.parent_issue_key}`);
                }}
                className="flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900 bg-purple-50 px-3 py-1.5 rounded-sm border border-purple-200 transition-colors"
              >
                <ArrowUpRight className="w-3.5 h-3.5" />
                Go to Initiative: {issue.parent_issue_key}
              </button>
            )}

            {/* Initiative Team Owners (only for initiatives with children) */}
            {issue.type === "INITIATIVE" && issue.children && issue.children.length > 0 && (
              <div className="space-y-2" data-testid="initiative-owners">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Team Owners
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {issue.children.map((child) => {
                    const childType = ISSUE_TYPE_CONFIG[child.type] || {};
                    const owner = getUserById(child.assignee_id);
                    return (
                      <div
                        key={child.id}
                        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-sm bg-gray-50"
                        data-testid={`owner-${child.type?.toLowerCase()}`}
                      >
                        <span
                          className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: childType.bg, color: childType.text }}
                        >
                          {childType.label}
                        </span>
                        {owner ? (
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Avatar className="h-5 w-5 border border-gray-200 flex-shrink-0">
                              <AvatarImage src={owner.avatar_url} alt={owner.full_name} />
                              <AvatarFallback className="text-[9px]">
                                {owner.full_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs text-gray-700 truncate">{owner.full_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Unassigned</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Fields Grid */}
            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Status
                </label>
                <Select
                  value={issue.status}
                  onValueChange={(val) => handleUpdate("status", val)}
                >
                  <SelectTrigger
                    className="rounded-sm h-9 text-sm"
                    data-testid="issue-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="rounded-sm text-sm">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: STATUS_COLORS[s]?.text }}
                          />
                          {STATUS_LABELS[s]}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Priority
                </label>
                <Select
                  value={issue.priority}
                  onValueChange={(val) => handleUpdate("priority", val)}
                >
                  <SelectTrigger
                    className="rounded-sm h-9 text-sm"
                    data-testid="issue-priority-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} className="rounded-sm text-sm">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: PRIORITY_COLORS[p]?.text }}
                          />
                          {p}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Assignee */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Assignee
                </label>
                <Select
                  value={issue.assignee_id || "unassigned"}
                  onValueChange={(val) =>
                    handleUpdate("assignee_id", val === "unassigned" ? null : val)
                  }
                >
                  <SelectTrigger
                    className="rounded-sm h-9 text-sm"
                    data-testid="issue-assignee-select"
                  >
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    <SelectItem value="unassigned" className="rounded-sm text-sm">
                      Unassigned
                    </SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} className="rounded-sm text-sm">
                        <div className="flex items-center gap-2">
                          <img
                            src={u.avatar_url}
                            alt={u.full_name}
                            className="w-4 h-4 rounded-full"
                          />
                          {u.full_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reporter */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Reporter
                </label>
                <Select
                  value={issue.reporter_id || "unassigned"}
                  onValueChange={(val) =>
                    handleUpdate("reporter_id", val === "unassigned" ? null : val)
                  }
                >
                  <SelectTrigger
                    className="rounded-sm h-9 text-sm"
                    data-testid="issue-reporter-select"
                  >
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm">
                    <SelectItem value="unassigned" className="rounded-sm text-sm">
                      Unassigned
                    </SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} className="rounded-sm text-sm">
                        <div className="flex items-center gap-2">
                          <img
                            src={u.avatar_url}
                            alt={u.full_name}
                            className="w-4 h-4 rounded-full"
                          />
                          {u.full_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Due Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="issue-due-date-picker"
                      className="flex items-center gap-2 w-full h-9 px-3 border border-gray-200 rounded-sm text-sm hover:border-brand-rose transition-colors text-left"
                    >
                      <CalendarDays className={`w-4 h-4 ${isPastDate(issue.due_date) ? "text-red-500" : "text-gray-400"}`} />
                      <span className={
                        issue.due_date
                          ? isPastDate(issue.due_date) ? "text-red-600 font-semibold" : "text-black"
                          : "text-gray-400"
                      }>
                        {issue.due_date
                          ? format(new Date(issue.due_date), "MMM d, yyyy")
                          : "Set due date"}
                      </span>
                      {isPastDate(issue.due_date) && (
                        <span className="text-[10px] font-bold text-red-600 ml-auto">OVERDUE</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-sm" align="start">
                    <Calendar
                      mode="single"
                      selected={issue.due_date ? new Date(issue.due_date) : undefined}
                      onSelect={(date) =>
                        handleUpdate("due_date", date ? format(date, "yyyy-MM-dd") : null)
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Release Date */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Release Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="issue-release-date-picker"
                      className="flex items-center gap-2 w-full h-9 px-3 border border-gray-200 rounded-sm text-sm hover:border-brand-rose transition-colors text-left"
                    >
                      <CalendarDays className={`w-4 h-4 ${isPastDate(issue.release_date) ? "text-red-500" : "text-gray-400"}`} />
                      <span className={
                        issue.release_date
                          ? isPastDate(issue.release_date) ? "text-red-600 font-semibold" : "text-black"
                          : "text-gray-400"
                      }>
                        {issue.release_date
                          ? format(new Date(issue.release_date), "MMM d, yyyy")
                          : "Set release date"}
                      </span>
                      {isPastDate(issue.release_date) && (
                        <span className="text-[10px] font-bold text-red-600 ml-auto">OVERDUE</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-sm" align="start">
                    <Calendar
                      mode="single"
                      selected={issue.release_date ? new Date(issue.release_date) : undefined}
                      onSelect={(date) =>
                        handleUpdate("release_date", date ? format(date, "yyyy-MM-dd") : null)
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Environment (only for Bugs) */}
              {issue.type === "BUG" && (
                <div className="space-y-1.5 col-span-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                    Environment
                  </label>
                  <Select
                    value={issue.environment || "none"}
                    onValueChange={(val) =>
                      handleUpdate("environment", val === "none" ? null : val)
                    }
                  >
                    <SelectTrigger
                      className="rounded-sm h-9 text-sm"
                      data-testid="issue-environment-select"
                    >
                      <SelectValue placeholder="Select environment" />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm">
                      <SelectItem value="none" className="rounded-sm text-sm">
                        Not Set
                      </SelectItem>
                      {ENVIRONMENTS.map((env) => {
                        const envColor = ENV_COLORS[env] || {};
                        return (
                          <SelectItem key={env} value={env} className="rounded-sm text-sm">
                            <div className="flex items-center gap-2">
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: envColor.text }}
                              />
                              {env}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Description
              </label>
              <textarea
                data-testid="issue-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleDescriptionBlur}
                placeholder="Add a description..."
                className="w-full min-h-[100px] p-3 border border-gray-200 rounded-sm text-sm resize-y focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
              />
            </div>

            {/* Attachment */}
            {issue.attachment_url?.trim() && (
              <div className="space-y-1.5" data-testid="issue-attachment">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Attachment
                </label>
                <a href={issue.attachment_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={issue.attachment_url}
                    alt="Attachment"
                    style={{ maxWidth: "100%" }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "block";
                    }}
                  />
                  <span style={{ display: "none" }} className="text-sm text-gray-400 italic">
                    Attachment unavailable
                  </span>
                </a>
              </div>
            )}

            {/* Child Issues (for Initiatives) */}
            {issue.children && issue.children.length > 0 && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5" />
                  Linked Tasks
                </label>
                <div className="border border-gray-200 rounded-sm divide-y divide-gray-100">
                  {issue.children.map((child) => {
                    const childType = ISSUE_TYPE_CONFIG[child.type] || {};
                    const childStatus = STATUS_COLORS[child.status] || {};
                    return (
                      <div
                        key={child.id}
                        data-testid={`child-issue-${child.issue_key}`}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => {
                          onClose();
                          const boardMap = {
                            DESIGN: "design",
                            FRONTEND: "frontend",
                            BACKEND: "backend",
                            QA: "backend",
                          };
                          navigate(
                            `/project/${child.project_key}/${boardMap[child.type] || "backend"}?issue=${child.issue_key}`
                          );
                        }}
                      >
                        <span
                          className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm"
                          style={{ backgroundColor: childType.bg, color: childType.text }}
                        >
                          {childType.label}
                        </span>
                        <span className="text-xs font-mono text-gray-400">
                          {child.issue_key}
                        </span>
                        <span className="text-sm text-black flex-1 truncate">
                          {child.title}
                        </span>
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm border"
                          style={{
                            backgroundColor: childStatus.bg,
                            color: childStatus.text,
                            borderColor: childStatus.border,
                          }}
                        >
                          {STATUS_LABELS[child.status]}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Comments */}
            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Comments
              </label>

              {/* Comment Input */}
              <div className="flex gap-3">
                {currentUser && (
                  <Avatar className="h-7 w-7 border border-gray-200 flex-shrink-0 mt-1">
                    <AvatarImage src={currentUser.avatar_url} alt={currentUser.full_name} />
                    <AvatarFallback className="text-[10px]">
                      {currentUser.full_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="flex-1 flex gap-2">
                  <MentionInput
                    value={newComment}
                    onChange={setNewComment}
                    onSubmit={handleAddComment}
                    users={users}
                    placeholder="Write a comment... Use @ to mention"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
                  />
                  <button
                    data-testid="comment-submit"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="bg-brand text-white hover:bg-brand-dark rounded-sm px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-40"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Comments List */}
              <div className="space-y-3">
                {comments.map((c) => {
                  const author = getUserById(c.author_id);
                  const isOwn = currentUser?.id === c.author_id;
                  const isEditing = editingCommentId === c.id;
                  return (
                    <div key={c.id} className="flex gap-3 group" data-testid={`comment-${c.id}`}>
                      <Avatar className="h-7 w-7 border border-gray-200 flex-shrink-0 mt-0.5">
                        <AvatarImage src={author?.avatar_url} alt={author?.full_name} />
                        <AvatarFallback className="text-[10px]">
                          {author?.full_name?.charAt(0) || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-black">
                            {author?.full_name || "Unknown"}
                          </span>
                          <span className="text-xs text-gray-400">
                            {format(new Date(c.created_at), "MMM d, h:mm a")}
                          </span>
                          {c.edited_at && (
                            <span className="text-xs text-gray-400 italic">(edited)</span>
                          )}
                          {isOwn && !isEditing && (
                            <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                data-testid={`comment-edit-${c.id}`}
                                onClick={() => {
                                  setEditingCommentId(c.id);
                                  setEditingCommentContent(c.content);
                                }}
                                className="p-1 hover:bg-gray-100 rounded-sm transition-colors"
                                title="Edit comment"
                              >
                                <Pencil className="w-3.5 h-3.5 text-gray-400 hover:text-brand" />
                              </button>
                              <button
                                data-testid={`comment-delete-${c.id}`}
                                onClick={() => handleDeleteComment(c.id)}
                                className="p-1 hover:bg-red-50 rounded-sm transition-colors"
                                title="Delete comment"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-600" />
                              </button>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="mt-1 flex gap-2">
                            <MentionInput
                              value={editingCommentContent}
                              onChange={setEditingCommentContent}
                              onSubmit={() => handleEditComment(c.id)}
                              users={users}
                              placeholder="Edit comment..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
                            />
                            <button
                              data-testid={`comment-save-${c.id}`}
                              onClick={() => handleEditComment(c.id)}
                              disabled={!editingCommentContent.trim()}
                              className="bg-brand text-white hover:bg-brand-dark rounded-sm px-2.5 py-2 text-sm transition-colors disabled:opacity-40"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              data-testid={`comment-cancel-edit-${c.id}`}
                              onClick={() => { setEditingCommentId(null); setEditingCommentContent(""); }}
                              className="border border-gray-300 rounded-sm px-2.5 py-2 text-sm hover:bg-gray-100 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700 mt-0.5">
                            <CommentContent content={c.content} users={users} />
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </>
  );
}
